<script setup lang='ts'>
import WisionLogo from '../WisionLogo.vue';
import Button from '../Button.vue';
import LockIcon from '../icons/LockIcon.vue';
import RightArrowIcon from '../icons/RightArrowIcon.vue';
import MapPinIcon from '../icons/MapPinIcon.vue';
import { useRouter, useRoute } from 'vue-router';
import { useAppState } from '../../store/state';
import DefaultAvatarIcon from '../icons/AvatarIcon.vue';
import SelectOrganization from '../SelectOrganization.vue';
import { ref, onMounted } from 'vue';
import useFieldValidation from '../../utilities/useFieldValidation';
import ScrollContainer from '../ScrollContainer.vue';

const fieldValidation = useFieldValidation();
const state = useAppState();
const router = useRouter();
const route = useRoute();
const showSelectOrg = ref(false);
const showUserAvatar = ref(state.avatar != undefined);
const tag = state.configTag;
const customers = state.access?.customers ?? [];
const selectedOrg = ref<number | undefined>(tag?.config?.organization_id);

const selectedOrgValid = fieldValidation.validation(selectedOrg);

if (!state.hasAccess) 
  router.push({ name: 'start' });
if (tag?.config?.organization_id && !customers.find(customer => customer.id === tag.config.organization_id)) {
  router.push({ name: 'no-access' });
}

const organizationLocked = !!tag?.config?.organization_id;
const user = state?.access?.name;

onMounted(() => {
  if (!state.configTag) {
    console.info('No tag available');
    return;
  }

  const uuid = state.configTag.uuid;

  state.fetchTagMeta(uuid);
});

const handlePlaceUnit = () => {
  fieldValidation.validateAll();
  if (fieldValidation.allFieldsValid.value) {
    state.setSelectedOrgId(selectedOrg.value as number);
    router.push({ name: 'map', params: { uuid: route.params.uuid } });
  }
};

const handleLogout = () => {
  const uuid = route.params.uuid as string;
  state.logout(`/onboard/${uuid}/start`);
};

const handleSelectOrg = (org : number) => {
  showSelectOrg.value = false;
  selectedOrg.value = org;
};

const setDefaultAvatar = () => {
  showUserAvatar.value = false;
};

const avatarPath = `/api/proxy/api/v3/user/avatar/${state.avatar}`;
</script>

<template>
  <div class="flex flex-col items-center w-full text-onboard-shade2">
    <ScrollContainer>
      <div :class="showSelectOrg && 'show'" class="select-org bg-onboard-background ">
        <SelectOrganization :onSelectOrganization="handleSelectOrg" :organizations="customers" :selectedOrganization="selectedOrg" />
      </div>
      <div class="flex flex-col mt-[20%] items-center">
        <WisionLogo :width="114" class="text-onboard-foreground"/>

        <span class="text-xl text-center mt-6 uppercase">
          {{ $t('nfc.welcome.title') }}
        </span>
        <div class="text-center mt-4 leading-none">
          <span class="text-sm uppercase">
            {{ $t('nfc.loggedInAs') }}
          </span>
        </div>
      </div>
      <div class="w-full mt-3">
        <div class="flex items-center justify-between w-full text-center border-2 border-onboard-shade2 p-3 rounded-xl">
          <div>
            <img v-if="showUserAvatar" class="w-11 h-11 rounded-full object-cover" :src="avatarPath" @error="setDefaultAvatar()" />
            <DefaultAvatarIcon v-else class="w-11 h-11"/>
          </div>
          <div class="truncate mx-2">
            {{ user }}
          </div>
          <div class="w-16 cursor-pointer">
            <div @click="handleLogout" class=" text-xs border-1 rounded-xl p-2">
              {{ $t('nfc.signout') }}
            </div>
          </div>
        </div>
        <div class="text-center my-6 leading-none">
          <span class="text-sm">
            {{ $t('nfc.selectOrg.long') }}
          </span>
        </div>

        <Button :class="[!selectedOrgValid && !organizationLocked && 'border-red-500', organizationLocked && 'opacity-50 pointer-events-none']" :disabled="organizationLocked" class="mt-3 h-16" @click="() => showSelectOrg = true">
          <template v-slot:iconLeft>
            <div></div>
          </template>
          {{ selectedOrg ? customers.find(customer => customer.id === selectedOrg)?.name : $t('nfc.selectOrg.short').toUpperCase() }}
          <template v-slot:iconRight>
            <LockIcon v-if="organizationLocked" />
            <RightArrowIcon v-if="!organizationLocked"/>
          </template>
        </Button>

        <Button class="mt-3 h-16" @click="handlePlaceUnit">
          <template v-slot:iconLeft>
            <MapPinIcon/>
          </template>
          {{ $t('nfc.placeDevice').toUpperCase() }}
          <template v-slot:iconRight>
            <RightArrowIcon/>
          </template>
        </Button>
      </div>
    </ScrollContainer>
    <span class="text-onboard-shade1 pt-5 pb-3 mt-auto text-center">WISION BUILD {{ state.build }}</span>
  </div>

</template>

<style lang="scss">
  .select-org {
    height: 100vh;
    position: fixed;
    width: 100%;
    z-index: 100;
    right: 0;
    transform: translate(100%);
    transition: transform 0.5s;

    &.show {
      transform: translate(0);
    }
  }
</style>
